import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseClipboardOptions {
  timeout?: number; // Reset copied state after timeout (ms)
}

export interface UseClipboardReturn {
  copied: boolean;
  copy: (text: string) => Promise<void>;
}

/**
 * Copy text to clipboard with auto-reset
 *
 * @example
 * ```typescript
 * const { copied, copy } = useClipboard({ timeout: 2000 });
 *
 * <button onClick={() => copy(apiKey)}>
 *   {copied ? 'Copied!' : 'Copy'}
 * </button>
 * ```
 */
export function useClipboard(options: UseClipboardOptions = {}): UseClipboardReturn {
  const { timeout = 2000 } = options;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const copy = useCallback(
    async (text: string) => {
      try {
        // Try modern Clipboard API first
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            document.execCommand('copy');
          } finally {
            document.body.removeChild(textArea);
          }
        }

        setCopied(true);

        // Clear any existing timeout
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }

        // Reset copied state after timeout
        if (timeout > 0) {
          timeoutRef.current = window.setTimeout(() => {
            setCopied(false);
            timeoutRef.current = null;
          }, timeout);
        }
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        setCopied(false);
      }
    },
    [timeout]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { copied, copy };
}
